import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { SetPermissionsAction } from '~permissions/actions/SetPermissionsAction';
import { PublishedConnectionTableExtractsRefreshNowAction } from '~src/app/spaces/ActionsMenu/EntityActions/PublishedConnectionTableExtractsRefreshNowAction';
import { ConnectedApplicationEditAction } from '~src/connectedApplications/actions/ConnectedApplicationActions';
import {
  DataAccelerationOffEntityAction,
  DataAccelerationOnEntityAction,
  DataAccelerationSubmenu
} from '~src/dataAcceleration/actions/DataAccelerationEntityActions';
import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { ResourceGovernanceEditSiteAction } from '~src/schedule/actions/ResourceGovernanceEditSiteAction';
import { ResourceGovernanceResetSiteToDefaultsAction } from '~src/schedule/actions/ResourceGovernanceResetSiteToDefaultAction';
import { ScheduleDisableLinkedTaskAction } from '~src/schedule/actions/ScheduleDisableLinkedTaskAction';
import { FeatureFlags } from '~src/ts/FeatureFlags';

import { CancelBackgroundJobAction } from '../../../backgroundJob/actions/CancelBackgroundJobAction';
import { UpdateDataFreshnessPolicyEntityAction } from '../../../dataAcceleration/actions/UpdateDataFreshnessPolicyEntityAction';
import { NewMetricEntityAction } from '../../../metrics/NewMetricEntityAction';
import { ScheduleDisableAction } from '../../../schedule/actions/ScheduleDisableAction';
import { ScheduleEditAction } from '../../../schedule/actions/ScheduleEditAction';
import { ScheduleEnableAction } from '../../../schedule/actions/ScheduleEnableAction';
import { ScheduleEnableLinkedTaskAction } from '../../../schedule/actions/ScheduleEnableLinkedTaskAction';
import { ScheduleRunNowAction } from '../../../schedule/actions/ScheduleRunNowAction';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { EditWorkbookAction } from '../../../workbook/entityActions/EditWorkbookAction';
import { NewWorkbookEntityAction } from '../../../workbook/entityActions/NewWorkbookEntityAction';
import { OpenWorkbookInDesktopAction } from '../../../workbook/entityActions/OpenWorkbookInDesktopAction';
import { isSubsetOf, KeySet, makeKeySet } from '../../ItemsSet';
import { AcknowledgeUserIdentityConflictAction } from './EntityActions/AcknowledgeUserIdentityConflictAction';
import { ActivateSiteAction } from './EntityActions/ActivateSiteAction';
import { ChangeCollectionVisibilityAction } from './EntityActions/ChangeCollectionVisibilityAction';
import { ChangeExtractDsToLiveAction } from './EntityActions/ChangeExtractDsToLiveAction';
import { ChangeExtractEmbeddedDsToLiveAction } from './EntityActions/ChangeExtractEmbeddedDsToLiveAction';
import { ChangeExtractRefreshScheduleEntityAction } from './EntityActions/ChangeExtractRefreshScheduleEntityAction';
import { ChangeRemoteExtractRefreshEntityAction } from './EntityActions/ChangeRemoteExtractRefreshEntityAction';
import { ChangeRemoteExtractRefreshExpectedDurationEntityAction } from './EntityActions/ChangeRemoteExtractRefreshExpectedDurationEntityAction';
import { ChangeSubscriptionDataConditionEntityAction } from './EntityActions/ChangeSubscriptionDataConditionEntityAction';
import { ChangeSubscriptionsFormatEntityAction } from './EntityActions/ChangeSubscriptionsFormatEntityAction';
import { ChangeSubscriptionsFrequencyEntityAction } from './EntityActions/ChangeSubscriptionsFrequencyEntityAction';
import { ChangeSubscriptionSubjectEntityAction } from './EntityActions/ChangeSubscriptionSubjectEntityAction';
import { CollectionDetailsAction } from './EntityActions/CollectionDetailsAction';
import {
  CreateEncryptedExtractForDsAction,
  CreateExtractForDsAction,
  CreateUnencryptedExtractForDsAction
} from './EntityActions/CreateExtractForDsAction';
import {
  CreateEncryptedExtractForEmbeddedDsAction,
  CreateExtractForEmbeddedDsAction,
  CreateUnencryptedExtractForEmbeddedDsAction
} from './EntityActions/CreateExtractForEmbeddedDsAction';
import { AdminAllowAccelerationAction } from './EntityActions/dataAcceleration/AdminAllowAccelerationAction';
import { AdminDisallowAccelerationAction } from './EntityActions/dataAcceleration/AdminDisallowAccelerationAction';
import { DataAlertAddMeAction } from './EntityActions/DataAlertAddMeAction';
import { DataAlertEditAction } from './EntityActions/DataAlertEditAction';
import { DataAlertRemoveMeAction } from './EntityActions/DataAlertRemoveMeAction';
import { DataAlertResumeAction } from './EntityActions/DataAlertResumeAction';
import { DecryptAction } from './EntityActions/DecryptAction';
import { DeleteAction } from './EntityActions/DeleteAction';
import { DeleteExtractRefreshEntityAction } from './EntityActions/DeleteExtractRefreshEntityAction';
import { DeleteRemoteExtractRefreshEntityAction } from './EntityActions/DeleteRemoteExtractRefreshEntityAction';
import { DeleteSiteAction } from './EntityActions/DeleteSiteAction';
import { DiscardDraftEntityAction } from './EntityActions/DiscardDraftEntityAction';
import { DownloadEntityAction } from './EntityActions/download/DownloadEntityAction';
import { EditDataConnectionsAction } from './EntityActions/EditDataConnectionAction';
import { EditDatasourceEntityAction } from './EntityActions/EditDatasourceEntityAction';
import { EditFlowEntityAction } from './EntityActions/EditFlowEntityAction';
import { EditFlowTaskAction } from './EntityActions/EditFlowTaskAction';
import { EditPublishedConnectionEntityAction } from './EntityActions/EditPublishedConnectionEntityAction';
import { EditSiteAction } from './EntityActions/EditSiteAction';
import { EditViewAction } from './EntityActions/EditViewAction';
import { EncryptAction } from './EntityActions/EncryptAction';
import {
  ExtractsEncryptionAction,
  ExtractsEncryptionEncryptedDsAction,
  ExtractsEncryptionUnencryptedAction
} from './EntityActions/ExtractsEncryptionAction';
import { GroupDatabasesAction } from './EntityActions/GroupDatabasesAction';
import { GroupDeleteAction } from './EntityActions/GroupDeleteAction';
import { GroupUpdateGrantLicenseModeAction } from './EntityActions/GroupsUpdateGrantLicenseModeAction';
import { GroupsUpdateMinimumSiteRoleAction } from './EntityActions/GroupsUpdateMinimumSiteRoleAction';
import { HideRecommendationAction } from './EntityActions/HideRecommendationAction';
import { IEntityAction } from './EntityActions/IEntityAction';
import { MoveAction } from './EntityActions/MoveAction';
import { NewFlowEntityAction } from './EntityActions/NewFlowEntityAction';
import { NewLensEntityAction } from './EntityActions/NewLensEntityAction';
import { NewPredictiveModelEntityAction } from './EntityActions/NewPredictiveModelEntityAction';
import { OpenManageItemsInCollectionsDialogEntityAction } from './EntityActions/OpenManageItemsInCollectionsDialog';
import { ReevaluateUserIdentityConflictAction } from './EntityActions/ReevaluateUserIdentityConflictAction';
import { RefreshExtractsAction } from './EntityActions/RefreshExtractsAction';
import { RemoveExternalContentAction } from './EntityActions/RemoveExternalContentAction';
import { RemoveFromThisCollectionAction } from './EntityActions/RemoveFromThisCollectionAction';
import {
  RenameCollectionAction,
  RenameDatasourceAction,
  RenameEntityAction,
  RenameGroupAction
} from './EntityActions/RenameEntityAction';
import { ResolveAmbiguousUserIdentityConflictAction } from './EntityActions/ResolveAmbiguousUserIdentityConflictAction';
import { ResumeExtractRefreshEntityAction } from './EntityActions/ResumeExtractRefreshEntityAction';
import { ResumeSubscriptionEntityAction } from './EntityActions/ResumeSubscriptionEntityAction';
import { RetryUserIdentityMigrationAction } from './EntityActions/RetryUserIdentityMigrationActions';
import { RevisionHistoryEntityAction } from './EntityActions/RevisionHistoryEntityAction';
import {
  RunExtractRefreshEntityAction,
  RunExtractRefreshEntityIfRefreshSSSEnabledAction
} from './EntityActions/RunExtractRefreshEntityAction';
import { RunFlowEntityAction } from './EntityActions/RunFlowEntityAction';
import { RunFlowTaskChangePriorityAction } from './EntityActions/RunFlowTaskChangePriorityAction';
import { RunFlowTaskChangeScheduleAction } from './EntityActions/RunFlowTaskChangeScheduleAction';
import { RunFlowTaskResumeAction } from './EntityActions/RunFlowTaskResumeAction';
import { RunFlowTaskRunNowAction } from './EntityActions/RunFlowTaskRunNowAction';
import { ScheduleRefreshWithBridgeEntityAction } from './EntityActions/ScheduleRefreshWithBridgeEntityAction';
import { ServerUserDeleteUsersAction } from './EntityActions/ServerUserDeleteUsersEntityAction';
import { ServerUserEditSiteMembershipUsersAction } from './EntityActions/ServerUserEditSiteMembershipEntityAction';
import { ServerUserForcePasswordupdateAction } from './EntityActions/ServerUserForcePasswordUpdateEntityAction';
import { ServerUserResetLoginRateLimitingAction } from './EntityActions/ServerUserResetLoginRateLimitingAction';
import {
  SetCertificationAction,
  SetCertificationDatabaseAndTableAction} from './EntityActions/SetCertificationAction';
import { SetCollectionOwnerEntityAction } from './EntityActions/SetCollectionOwnerAction';
import { SetContactAction } from './EntityActions/SetContactAction';
import {
  DataQualitySubmenu,
  SetDataQualityAction,
  SetDataQualityTriggerAction
} from './EntityActions/SetDataQualityAction';
import {
  SetDescriptionAction,
  SetMetricDescriptionAction,
  SetPublishedConnectionDescriptionAction,
  SetPublishedConnectionTableDescriptionAction
} from './EntityActions/SetDescriptionAction';
import { SetDisplayTabsAction } from './EntityActions/SetDisplayTabsAction';
import { SetExtractRefreshPriorityEntityAction } from './EntityActions/SetExtractRefreshPriorityEntityAction';
import { SetOwnerAction } from './EntityActions/SetOwnerAction';
import { SetTagsAction } from './EntityActions/SetTagsAction';
import { ShareContentAction } from './EntityActions/ShareContentAction';
import { SiteUserEditGroupMembershipAction } from './EntityActions/SiteUserEditGroupMembershipEntityAction';
import { SiteUserForcePasswordUpdateAction } from './EntityActions/SiteUserForcePasswordUpdateEntityAction';
import { SiteUserRemoveAction } from './EntityActions/SiteUserRemoveEntityAction';
import { SiteUserRemoveFromGroupAction } from './EntityActions/SiteUserRemoveFromGroupEntityAction';
import { SiteUserResetLoginRateLimitingAction } from './EntityActions/SiteUserResetLoginRateLimitingEntityAction';
import { SiteUserSetAuthenticationAction } from './EntityActions/SiteUserSetAuthenticationEntityAction';
import { SiteUserSiteRoleAction } from './EntityActions/SiteUserSiteRoleEntityAction';
import { SuspendSiteAction } from './EntityActions/SuspendSiteAction';
import { SyncActiveDirectoryGroupsAction } from './EntityActions/SyncActiveDirectoryGroupsAction';
import { UngroupDatabasesAction } from './EntityActions/UngroupDatabasesAction';
import { UnsubsribeFromSubscriptionsEntityAction } from './EntityActions/UnsubscribeFromSubscriptionsEntityAction';
import { WhoHasSeenThisViewEntityAction } from './EntityActions/WhoHasSeenThisViewEntityAction';

interface IEntityActionListEntry<T extends ExplorableItemType> {
  action: IEntityAction<IExplorableItemTypeInfo<T>, any>;
  supportsMultipleItems: boolean;
  contentTypes: KeySet<T>;
  subEntityActionList?: IEntityActionListEntry<T>[];
}

interface ContentActionsSectionsProps {
  collectionsPermissionsEnabled: boolean;
  linkedTaskSchedulingEnabled: boolean;
}

/**
 * This function does nothing useful at runtime, but it enforces compile-time self-consistency of the
 * type parameter for each list entry, particularly that the `action` and `contentTypes` are both
 * applicable to the same values of the type parameter T, since we can't achieve that specificity
 * in the array type declaration for ContentActionsSections.
 */
function validEntry<T extends ExplorableItemType>(entry: IEntityActionListEntry<T>): IEntityActionListEntry<T> {
  return entry;
}

/**
 * This is a list of all the entity actions that can appear in actions menus (in explorers or mastheads).
 * The top level arrays denote "sections" separated in the menu by a horizontal divider.
 *
 * When the actions menu is displayed, this list gets filtered to show only the actions that apply to the selected items.
 *
 * If the entity actions should change based on Feature Flag or based on any value that can be overridden at runtime,
 * such value must be passed as parameter for the entity actions to be evaluated correctly.
 */
const ContentActionsSections = (props: ContentActionsSectionsProps): IEntityActionListEntry<ExplorableItemType>[][] => [
  [
    validEntry({ action: EditDatasourceEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
  ],
  [
    validEntry({ action: NewWorkbookEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: NewFlowEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: NewLensEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: NewMetricEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: NewPredictiveModelEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: EditFlowEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('flow') }),
    validEntry({ action: RunFlowEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('flow') }),
  ],
  [
    validEntry({ action: SiteUserEditGroupMembershipAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserSiteRoleAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserSetAuthenticationAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserRemoveAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserForcePasswordUpdateAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserResetLoginRateLimitingAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
    validEntry({ action: SiteUserRemoveFromGroupAction, supportsMultipleItems: true, contentTypes: makeKeySet('siteuser') }),
  ],
  [
    validEntry({ action: SyncActiveDirectoryGroupsAction, supportsMultipleItems: true, contentTypes: makeKeySet('group') }),
    validEntry({ action: RenameGroupAction, supportsMultipleItems: false, contentTypes: makeKeySet('group') }),
    validEntry({ action: GroupsUpdateMinimumSiteRoleAction, supportsMultipleItems: true, contentTypes: makeKeySet('group') }),
    validEntry({ action: GroupUpdateGrantLicenseModeAction, supportsMultipleItems: true, contentTypes: makeKeySet('group') }),
    validEntry({ action: GroupDeleteAction, supportsMultipleItems: true, contentTypes: makeKeySet('group') }),
  ],
  [
    validEntry({ action: ServerUserEditSiteMembershipUsersAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserDeleteUsersAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserForcePasswordupdateAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
    validEntry({ action: ServerUserResetLoginRateLimitingAction, supportsMultipleItems: true, contentTypes: makeKeySet('serveruser') }),
  ],
  [
    validEntry({ action: EditPublishedConnectionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('publishedConnection') }),
    validEntry({ action: EditWorkbookAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') }),
    validEntry({ action: DiscardDraftEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') }),
    validEntry({ action: EditViewAction, supportsMultipleItems: false, contentTypes: makeKeySet('view') }),
    validEntry({ action: OpenWorkbookInDesktopAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'view') }),
  ],
  [
    validEntry({ action: ConnectedApplicationEditAction,    supportsMultipleItems: false, contentTypes: makeKeySet('connectedApplication')})
  ],
  [
    validEntry({ action: ShareContentAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'project', 'view', 'datasource', 'flow', 'recommendedView', 'metric', 'collection') }),
    validEntry({ action: DownloadEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'datasource', 'flow') }),
    validEntry({ action: HideRecommendationAction, supportsMultipleItems: false, contentTypes: makeKeySet('recommendedView') }),
  ],
  [
    validEntry({
      action: OpenManageItemsInCollectionsDialogEntityAction, supportsMultipleItems: true,
      contentTypes: makeKeySet('workbook', 'datasource', 'view', 'flow', 'metric', 'project', 'publishedConnection', 'publishedConnectionTable', 'dataRole' /*, 'table', 'database' TFS 1089949*/)
    }),
    validEntry({
      action: RemoveFromThisCollectionAction, supportsMultipleItems: true,
      contentTypes: makeKeySet('workbook', 'datasource', 'view', 'flow', 'metric', 'project', 'publishedConnection', 'publishedConnectionTable', 'dataRole' /*, 'table', 'database' TFS 1089949*/)
    })
  ],
  [
    validEntry({ action: DataAlertAddMeAction, supportsMultipleItems: true, contentTypes: makeKeySet('dataalert') }),
    validEntry({ action: DataAlertRemoveMeAction, supportsMultipleItems: true, contentTypes: makeKeySet('dataalert') }),
    validEntry({ action: DataAlertResumeAction, supportsMultipleItems: false, contentTypes: makeKeySet('dataalert') }),
    validEntry({ action: DataAlertEditAction, supportsMultipleItems: false, contentTypes: makeKeySet('dataalert') }),
  ],
  [
    validEntry({ action: ScheduleRunNowAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
    validEntry({ action: ScheduleEnableAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
    validEntry({ action: ScheduleDisableAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
    validEntry({ action: ScheduleEditAction, supportsMultipleItems: false, contentTypes: makeKeySet('schedule') }),
  ].concat(
    areLinkedTasksEnabled() ?
      [
        validEntry({ action: ScheduleEnableLinkedTaskAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
        validEntry({ action: ScheduleDisableLinkedTaskAction, supportsMultipleItems: true, contentTypes: makeKeySet('schedule') }),
      ] : []),
  [
    validEntry({ action: SetTagsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource', 'view', 'flow', 'metric', 'database', 'databaseWithCrass', 'table', 'tableWithCrass') }),
    validEntry({ action: RenameEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'project', 'flow', 'schedule', 'metric', 'publishedConnection') }),
    validEntry({ action: MoveAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow', 'dataRole', 'lens', 'metric', 'publishedConnection') }),
    validEntry({ action: RenameDatasourceAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),

    validEntry({ action: SetDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'databaseWithCrass', 'table', 'tableWithCrass') }),
    validEntry({ action: SetMetricDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('metric') }),
    validEntry({ action: SetPublishedConnectionDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('publishedConnection') }),
    validEntry({ action: SetPublishedConnectionTableDescriptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('publishedConnectionTable') }),

    // Set Permissions can be performed on multiple items of the same type, but not multiple items of different types,
    // so we're representing it as separate list entries to facilitate that.
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('project') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('view') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('flow') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('dataRole') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('lens') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('table') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('tableWithCrass') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('database') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('databaseWithCrass') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('metric') }),
    validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('publishedConnection') }),

    validEntry({ action: WhoHasSeenThisViewEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('view') }),

    validEntry({ action: SetOwnerAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'flow', 'dataRole', 'lens', 'dataalert', 'metric', 'publishedConnection', 'customView') }),
    validEntry({ action: SetContactAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'databaseWithCrass', 'table', 'tableWithCrass') }),
    validEntry({ action: GroupDatabasesAction, supportsMultipleItems: true, contentTypes: makeKeySet('database', 'databaseWithCrass') }),
    validEntry({ action: UngroupDatabasesAction, supportsMultipleItems: true, contentTypes: makeKeySet('database', 'databaseWithCrass') }),
  ],
  [
    validEntry({ action: SetCertificationAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: SetCertificationDatabaseAndTableAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'databaseWithCrass', 'table', 'tableWithCrass', 'tableWithCrass') }),
    validEntry({
      action: DataQualitySubmenu, supportsMultipleItems: false, contentTypes: makeKeySet('datasource', 'flow', 'database', 'databaseWithCrass', 'table', 'tableWithCrass'),
      subEntityActionList: [
        validEntry({ action: SetDataQualityAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource', 'flow', 'database', 'databaseWithCrass', 'table', 'tableWithCrass') }),
        validEntry({ action: SetDataQualityTriggerAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource', 'flow') }),
      ]
    }),
  ],
  [
    validEntry({ action: RemoveExternalContentAction, supportsMultipleItems: false, contentTypes: makeKeySet('database', 'databaseWithCrass', 'table', 'tableWithCrass') }),
  ],
  [
    validEntry({ action: EditDataConnectionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource', 'dataconnection') }),
    validEntry({ action: SetDisplayTabsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook') }),
    validEntry({ action: ChangeExtractDsToLiveAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
    validEntry({
      action: CreateExtractForDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource'),
      subEntityActionList: [
        validEntry({ action: CreateEncryptedExtractForDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') }),
        validEntry({ action: CreateUnencryptedExtractForDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('datasource') })
      ]
    }),
    validEntry({ action: ChangeExtractEmbeddedDsToLiveAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
    validEntry({
      action: CreateExtractForEmbeddedDsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource'),
      subEntityActionList: [
        validEntry({ action: CreateEncryptedExtractForEmbeddedDsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
        validEntry({ action: CreateUnencryptedExtractForEmbeddedDsAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') })
      ]
    }),
    validEntry({
      action: ExtractsEncryptionAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook'),
      subEntityActionList: [
        validEntry({ action: ExtractsEncryptionEncryptedDsAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') }),
        validEntry({ action: ExtractsEncryptionUnencryptedAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook') })
      ]
    }),
    validEntry({ action: RefreshExtractsAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource') }),
    validEntry({ action: PublishedConnectionTableExtractsRefreshNowAction, supportsMultipleItems: true, contentTypes: makeKeySet('publishedConnectionTable') }),
    validEntry({ action: RunExtractRefreshEntityIfRefreshSSSEnabledAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource') }),
    validEntry({ action: ScheduleRefreshWithBridgeEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('datasource') }),
    validEntry({ action: EncryptAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource', 'flow') }),
    validEntry({ action: DecryptAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'datasource', 'flow') }),
  ],
  [
    validEntry({ action: RunFlowTaskRunNowAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
  ],
  [
    validEntry({ action: EditFlowTaskAction, supportsMultipleItems: false, contentTypes: makeKeySet('runFlowTask')}),
    validEntry({ action: RunFlowTaskChangeScheduleAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
    validEntry({ action: RunFlowTaskChangePriorityAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
    validEntry({ action: RunFlowTaskResumeAction, supportsMultipleItems: true, contentTypes: makeKeySet('runFlowTask') }),
  ],
  [
    validEntry({ action: CollectionDetailsAction, supportsMultipleItems: false, contentTypes: makeKeySet('collection') }),
    validEntry({ action: RenameCollectionAction, supportsMultipleItems: false, contentTypes: makeKeySet('collection') }),
    props.collectionsPermissionsEnabled
      ? validEntry({ action: SetPermissionsAction, supportsMultipleItems: true, contentTypes: makeKeySet('collection') })
      : validEntry({ action: ChangeCollectionVisibilityAction, supportsMultipleItems: false, contentTypes: makeKeySet('collection') }),
    validEntry({ action: SetCollectionOwnerEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('collection') }),
  ],
  [
    validEntry({ action: RevisionHistoryEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook', 'datasource', 'flow', 'publishedConnection') }),
    validEntry({ action: DeleteAction, supportsMultipleItems: true, contentTypes: makeKeySet('workbook', 'project', 'datasource', 'view', 'flow', 'dataRole', 'lens', 'metric', 'dataalert', 'schedule', 'runFlowTask', 'publishedConnection', 'collection', 'customView', 'connectedApplication') }),
  ],
  [
    validEntry({ action: ResumeSubscriptionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionsFrequencyEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionSubjectEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionDataConditionEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: ChangeSubscriptionsFormatEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription') }),
    validEntry({ action: UnsubsribeFromSubscriptionsEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('subscription') }),
  ],
  [
    validEntry({ action: RunExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: ResumeExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: ChangeExtractRefreshScheduleEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: SetExtractRefreshPriorityEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
    validEntry({ action: DeleteExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('extractTask') }),
  ],
  [
    validEntry({ action: AdminAllowAccelerationAction, supportsMultipleItems: true, contentTypes: makeKeySet('accelerationTask') }),
    validEntry({ action: AdminDisallowAccelerationAction, supportsMultipleItems: true, contentTypes: makeKeySet('accelerationTask') }),
  ],
  [
    validEntry({ action: ResolveAmbiguousUserIdentityConflictAction, supportsMultipleItems: false, contentTypes: makeKeySet('userIdentityConflict') }),
    validEntry({ action: RetryUserIdentityMigrationAction, supportsMultipleItems: true, contentTypes: makeKeySet('userIdentityConflict') }),
    validEntry({ action: AcknowledgeUserIdentityConflictAction, supportsMultipleItems: true, contentTypes: makeKeySet('userIdentityConflict') }),
    validEntry({ action: ReevaluateUserIdentityConflictAction, supportsMultipleItems: true, contentTypes: makeKeySet('userIdentityConflict') }),
  ],
  [
    validEntry({ action: DeleteRemoteExtractRefreshEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('remoteRefreshSchedule') }),
    validEntry({ action: ChangeRemoteExtractRefreshEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('remoteRefreshSchedule') }),
    validEntry({ action: ChangeRemoteExtractRefreshExpectedDurationEntityAction, supportsMultipleItems: true, contentTypes: makeKeySet('remoteRefreshSchedule') }),
  ],
  [
    validEntry({ action: CancelBackgroundJobAction, supportsMultipleItems: false, contentTypes: makeKeySet('backgroundJob') }),
  ],
  [
    validEntry({ action: ActivateSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
    validEntry({ action: SuspendSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
    validEntry({ action: EditSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
    validEntry({ action: DeleteSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('site') }),
  ],
  [
    validEntry({ action: DataAccelerationSubmenu,   supportsMultipleItems: true, contentTypes: makeKeySet('view', 'workbook'),
      subEntityActionList: [
        validEntry({ action: DataAccelerationOnEntityAction,    supportsMultipleItems: true, contentTypes: makeKeySet('view', 'workbook') }),
        validEntry({ action: DataAccelerationOffEntityAction,   supportsMultipleItems: true, contentTypes: makeKeySet('view', 'workbook') }),
      ]
    }),
  ],
  [
    validEntry({ action: UpdateDataFreshnessPolicyEntityAction, supportsMultipleItems: false, contentTypes: makeKeySet('workbook')}),
  ],
  [
    validEntry({ action: ResourceGovernanceResetSiteToDefaultsAction, supportsMultipleItems: true, contentTypes: makeKeySet('resourceGovernanceSetting')}),
    validEntry({ action: ResourceGovernanceEditSiteAction, supportsMultipleItems: true, contentTypes: makeKeySet('resourceGovernanceSetting')}),
  ]
];

const memoizedContentActionsSections = defaultMemoize(ContentActionsSections);

export function getActionSectionsForItems(...items: IExplorableItemTypeInfo[]): IEntityAction<IExplorableItemTypeInfo, any>[][] {
  const itemTypes = makeKeySet(...items.map(item => item.contentType));
  const contentActionSectionsProps: ContentActionsSectionsProps = {
    collectionsPermissionsEnabled: FeatureFlags.collectionsPermissionsEnabled,
    linkedTaskSchedulingEnabled: areLinkedTasksEnabled(),
  };
  const sections = memoizedContentActionsSections(contentActionSectionsProps);
  const entityActionSections = sections.map(section => {
    return [].concat(section, _.flatten(section.filter(info => !!info.subEntityActionList).map(info => info.subEntityActionList.map(subEntity => {
      subEntity.action.parentName = info.action.name; return subEntity;
    })))).filter(info => isSubsetOf(itemTypes, info.contentTypes))
      .filter(info => info.supportsMultipleItems || items.length === 1)
      .map(info => info.action);
  });

  return entityActionSections.filter(section => !_(section).isEmpty());
}
